import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Spinner } from './spinner';
import { ProgressBar } from './progress-bar';

export interface LoadingOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the overlay is visible */
  visible?: boolean;
  /** Fullscreen mode (covers entire viewport) or inline (covers parent container) */
  fullscreen?: boolean;
  /** Loading message to display */
  message?: string;
  /** Optional progress value (0-100) */
  progress?: number;
  /** Show progress bar instead of or with spinner */
  showProgress?: boolean;
  /** Blur intensity for backdrop */
  blurIntensity?: 'none' | 'sm' | 'md' | 'lg';
  /** Allow interaction with underlying content */
  allowInteraction?: boolean;
  /** Animation duration in ms */
  animationDuration?: number;
  /** Spinner size */
  spinnerSize?: 'sm' | 'md' | 'lg';
  /** Custom content instead of default spinner/progress */
  children?: React.ReactNode;
}

const blurClasses = {
  none: '',
  sm: 'backdrop-blur-sm',
  md: 'backdrop-blur-md',
  lg: 'backdrop-blur-lg',
};

export function LoadingOverlay({
  visible = true,
  fullscreen = false,
  message,
  progress,
  showProgress = false,
  blurIntensity = 'md',
  allowInteraction = false,
  animationDuration = 200,
  spinnerSize = 'lg',
  children,
  className,
  ...props
}: LoadingOverlayProps) {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('common:common.loading');
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(visible);

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, animationDuration);
      return () => clearTimeout(timer);
    }
  }, [visible, animationDuration]);

  if (!shouldRender) return null;

  const overlayContent = (
    <div
      className={cn(
        // Base styles
        'flex flex-col items-center justify-center gap-4',
        // Positioning
        fullscreen ? 'fixed inset-0' : 'absolute inset-0',
        // Background and blur
        'bg-bg-void/60',
        blurClasses[blurIntensity],
        // Z-index
        fullscreen ? 'z-[9999]' : 'z-overlay',
        // Interaction
        !allowInteraction && 'pointer-events-auto',
        allowInteraction && 'pointer-events-none',
        // Animation
        'transition-all ease-standard',
        isVisible ? 'opacity-100' : 'opacity-0',
        // Reduced motion
        'motion-reduce:transition-none',
        className
      )}
      style={{
        transitionDuration: `${animationDuration}ms`,
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      {...props}
    >
      <div
        className={cn(
          'flex flex-col items-center gap-4 p-6 rounded-lg',
          'bg-bg-elevated/90 border border-border shadow-xl',
          // Allow interaction with just the content box if enabled
          !allowInteraction && 'pointer-events-auto',
          // Scale animation
          'transition-transform ease-emphasized',
          isVisible ? 'scale-100' : 'scale-95',
          'motion-reduce:transition-none'
        )}
        style={{
          transitionDuration: `${animationDuration}ms`,
        }}
      >
        {children ? (
          children
        ) : (
          <>
            {!showProgress && progress === undefined && (
              <Spinner
                size={spinnerSize === 'sm' ? 'md' : spinnerSize === 'md' ? 'lg' : 'xl'}
                variant="primary"
              />
            )}

            {(showProgress || progress !== undefined) && (
              <div className="w-48">
                <ProgressBar
                  value={progress}
                  indeterminate={progress === undefined}
                  size="md"
                  variant="primary"
                  showPercentage={progress !== undefined}
                />
              </div>
            )}

            {resolvedMessage && (
              <p className="text-fg-secondary text-sm text-center max-w-xs">
                {resolvedMessage}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (!fullscreen) {
    // For inline mode, ensure parent has relative positioning
    return overlayContent;
  }

  // For fullscreen mode, portal to body
  return overlayContent;
}

// Hook for managing loading overlay state
export function useLoadingOverlay(initialVisible = false) {
  const [visible, setVisible] = useState(initialVisible);
  const { t } = useTranslation();
  const defaultMessage = t('common:common.loading');
  const [message, setMessage] = useState<string>(defaultMessage);
  const [progress, setProgress] = useState<number | undefined>();

  const show = (msg?: string, prog?: number) => {
    if (msg) setMessage(msg);
    if (prog !== undefined) setProgress(prog);
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
    // Reset after animation
    setTimeout(() => {
      setMessage(defaultMessage);
      setProgress(undefined);
    }, 200);
  };

  const updateProgress = (prog: number, msg?: string) => {
    setProgress(prog);
    if (msg) setMessage(msg);
  };

  return {
    visible,
    message,
    progress,
    show,
    hide,
    updateProgress,
    setMessage,
  };
}

// Compound component for specific use cases
export function PageLoadingOverlay({ message }: { message?: string }) {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('common:common.loadingPage');

  return <LoadingOverlay visible fullscreen message={resolvedMessage} spinnerSize="lg" />;
}

export function DataLoadingOverlay({
  message,
  progress,
}: {
  message?: string;
  progress?: number;
}) {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('common:common.loadingData');

  return (
    <LoadingOverlay
      visible
      fullscreen={false}
      message={resolvedMessage}
      progress={progress}
      showProgress
      spinnerSize="md"
    />
  );
}

// Wrapper component to add loading overlay to any container
export interface WithLoadingOverlayProps {
  loading?: boolean;
  message?: string;
  progress?: number;
  children: React.ReactNode;
  className?: string;
}

export function WithLoadingOverlay({
  loading = false,
  message,
  progress,
  children,
  className,
}: WithLoadingOverlayProps) {
  return (
    <div className={cn('relative', className)}>
      {children}
      <LoadingOverlay
        visible={loading}
        fullscreen={false}
        message={message}
        progress={progress}
        showProgress={progress !== undefined}
      />
    </div>
  );
}
